import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { KrCard } from '../card/KrCard';

export const StatCard = ({
  title,
  label,
  icon,
  val,
  isAmt,
  pctChange,
  vsLabel,
  isVs = true,
  cat,
  showPercent = true,
  footnote,
}) => {
  const theme = useTheme();

  const isUp = pctChange > 0;
  const isDown = pctChange < 0;

  const pillBg =
    isUp ? alpha(theme.palette.success.main, 0.16)
    : isDown ? alpha(theme.palette.error.main, 0.16)
    : alpha(theme.palette.text.secondary, 0.12);

  const pillIcon =
    isUp ? fas.faArrowUp
    : isDown ? fas.faArrowDown
    : fas.faMinus;

  const amtText = isAmt ? formatAmt(val) : formatUnits(val);

  return (
    <KrCard title={title} icon={icon}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end', // pushes everything to the bottom
        }}
      >
        {label && (
          <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            {label}
          </Typography>
        )}
        <Box sx={{ position: 'relative' }}>
          <Typography
            variant="h3"
            sx={{
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cat ?? amtText}
          </Typography>
          {/* Hangs below the value, so cards with and without a footnote keep their values aligned */}
          {footnote && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                mt: -0.75,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {footnote.label}: {isAmt ? formatAmt(footnote.val) : formatUnits(footnote.val)}
            </Typography>
          )}
        </Box>
        {pctChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* bigger pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25, // wider
                py: 0.5, // taller
                bgcolor: pillBg,
                borderRadius: 1.25,
                fontSize: 14, // larger than caption
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              <FontAwesomeIcon icon={pillIcon} />
              {pctString(pctChange, showPercent)}
            </Box>

            {/* vs label matches pill’s size/weight (but neutral color) */}
            <Box
              sx={{
                fontSize: 14,
                // fontWeight: 600,
                color: 'text.secondary',
                lineHeight: 1.5,
              }}
            >
              {isVs ? `vs` : `for`} {vsLabel}
            </Box>
          </Box>
        )}
      </Box>
    </KrCard>
  );
};

/* ---------- helpers ---------- */

const formatAmt = (value) => {
  if (value == null || isNaN(value)) return '$-';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  const units = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
  ];

  let divisor = 1;
  let suffix = '';
  for (const u of units) {
    if (abs >= u.limit) {
      divisor = u.limit;
      suffix = u.suffix;
      break;
    }
  }

  const scaled = abs / divisor;
  let str = Number(scaled).toPrecision(3);
  str = String(+str); // drop trailing zeros / unnecessary decimals

  return `${sign}$${str}${suffix}`;
};

const formatUnits = (value) => {
  if (value == null || isNaN(value)) return '-';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  const units = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
  ];

  let divisor = 1;
  let suffix = '';
  for (const u of units) {
    if (abs >= u.limit) {
      divisor = u.limit;
      suffix = u.suffix;
      break;
    }
  }

  const scaled = abs / divisor;
  let str = Number(scaled).toPrecision(3);
  str = String(+str); // drop trailing zeros / unnecessary decimals

  return `${sign}${str}${suffix}`;
};

const pctString = (pct, showPercent = true) => {
  if (pct == null || isNaN(pct)) return showPercent ? '–%' : '–';
  const val = Math.round(pct * 100) / 100; // round to 2 decimals
  return showPercent ? `${val}%` : `${val}`;
};
